import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Gift, 
  Users, 
  Copy, 
  Share2, 
  CheckCircle, 
  Lock, 
  Trophy,
  Twitter,
  Facebook,
  MessageCircle,
  Mail,
  ArrowLeft,
  Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ReferralDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      toast.error('Please sign in to view your referrals');
      return;
    }
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/referral/my-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading referral stats:', error);
      toast.error('Failed to load referral stats');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(`Check out theailedger.ai - The Marketplace for AI Products! 🚀\n\nJoin using my link and we both get rewards:`);
    const url = encodeURIComponent(stats?.referral_link || '');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(stats?.referral_link || '');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Check out theailedger.ai - The Marketplace for AI Products! Join using my link: ${stats?.referral_link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out theailedger.ai - AI Marketplace');
    const body = encodeURIComponent(`Hey!\n\nI found this amazing marketplace for AI products and thought you'd love it.\n\nJoin using my referral link and we both get rewards:\n${stats?.referral_link}\n\nSee you there!`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const claimReward = async (tierId) => {
    setClaiming(tierId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/referral/claim-reward/${tierId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadStats(); // Reload stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-pulse text-[#00F0FF]">Loading referral stats...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center py-12 text-neutral-400">
          Unable to load referral stats. Please try again.
        </div>
      </div>
    );
  }

  const progressPercent = stats.next_tier 
    ? (stats.referral_count / stats.next_tier.referrals_required) * 100 
    : 100;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation Header */}
      <header className="bg-neutral-900/80 border-b border-white/10 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <h1 className="text-lg font-bold">Referral Program</h1>
          <button
            onClick={() => navigate('/')}
            className="text-neutral-400 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Stats Header */}
      <div className="bg-gradient-to-r from-white/20 to-[#00F0FF]/20 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Gift className="w-8 h-8 text-[#00F0FF]" />
          <div>
            <h2 className="text-2xl font-bold">Your Referrals</h2>
            <p className="text-neutral-400 text-sm">
              {stats.user_type === 'creator' ? 'Invite creators & earn rewards' : 'Invite friends & earn rewards'}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-white" />
            <div className="text-2xl font-bold">{stats.referral_count}</div>
            <div className="text-xs text-neutral-400">Referrals</div>
          </div>
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-[#00F0FF]" />
            <div className="text-2xl font-bold">{stats.unlocked_tiers.length}</div>
            <div className="text-xs text-neutral-400">Tiers Unlocked</div>
          </div>
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <Gift className="w-6 h-6 mx-auto mb-2 text-[#00FF94]" />
            <div className="text-2xl font-bold">${stats.credits}</div>
            <div className="text-xs text-neutral-400">Credits Earned</div>
          </div>
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-[#00F0FF]" />
            <div className="text-2xl font-bold">{stats.badges.length}</div>
            <div className="text-xs text-neutral-400">Badges</div>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-[#00F0FF]" />
          Your Referral Link
        </h3>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-neutral-300 truncate">
            {stats.referral_link}
          </div>
          <Button 
            onClick={copyLink}
            className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
        </div>

        {/* Share Buttons */}
        <div className="flex flex-wrap gap-3 mt-4">
          <Button 
            onClick={shareOnTwitter}
            variant="outline" 
            className="border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Twitter
          </Button>
          <Button 
            onClick={shareOnFacebook}
            variant="outline"
            className="border-[#4267B2] text-[#4267B2] hover:bg-[#4267B2]/10"
          >
            <Facebook className="w-4 h-4 mr-2" />
            Facebook
          </Button>
          <Button 
            onClick={shareOnWhatsApp}
            variant="outline"
            className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button 
            onClick={shareViaEmail}
            variant="outline"
            className="border-neutral-400 text-neutral-400 hover:bg-neutral-400/10"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
        </div>
      </div>

      {/* Progress to Next Tier */}
      {stats.next_tier && (
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
          <h3 className="font-semibold mb-4">Progress to Next Reward</h3>
          <div className="mb-2 flex justify-between text-sm">
            <span>{stats.referral_count} referrals</span>
            <span>{stats.next_tier.referrals_required} needed</span>
          </div>
          <div className="w-full bg-neutral-800 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-[#00F0FF] to-white h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
          </div>
          <p className="text-sm text-neutral-400 mt-2">
            <span className="text-[#00F0FF] font-semibold">{stats.referrals_to_next_tier}</span> more referrals to unlock: {stats.next_tier.description}
          </p>
        </div>
      )}

      {/* Reward Tiers */}
      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#00F0FF]" />
          {stats.user_type === 'creator' ? 'Creator Rewards' : 'Reward Tiers'}
        </h3>
        
        <div className="space-y-3">
          {stats.tiers.map((tier, index) => {
            const isUnlocked = stats.referral_count >= tier.referrals_required;
            const isClaimed = stats.unlocked_tiers.some(t => t.tier_id === tier.tier_id) || 
                            (stats.claimed_referral_tiers || []).includes(tier.tier_id);
            
            return (
              <div 
                key={tier.tier_id}
                className={`flex items-center justify-between p-4 rounded-xl border ${
                  isUnlocked 
                    ? 'bg-[#00FF94]/10 border-[#00FF94]/30' 
                    : 'bg-black/30 border-white/5'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isUnlocked ? 'bg-[#00FF94]/20' : 'bg-neutral-800'
                  }`}>
                    {isUnlocked ? (
                      <CheckCircle className="w-5 h-5 text-[#00FF94]" />
                    ) : (
                      <Lock className="w-5 h-5 text-neutral-500" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{tier.description}</div>
                    <div className="text-sm text-neutral-400">
                      {tier.referrals_required} referral{tier.referrals_required > 1 ? 's' : ''} required
                    </div>
                  </div>
                </div>
                
                {isUnlocked && !isClaimed && (
                  <Button
                    onClick={() => claimReward(tier.tier_id)}
                    disabled={claiming === tier.tier_id}
                    size="sm"
                    className="bg-[#00FF94] hover:bg-[#00CC77] text-black font-semibold"
                  >
                    {claiming === tier.tier_id ? 'Claiming...' : 'Claim'}
                  </Button>
                )}
                
                {isClaimed && (
                  <span className="text-[#00FF94] text-sm font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Claimed
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Referrals */}
      {stats.referrals.length > 0 && (
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-white" />
            Your Referrals
          </h3>
          
          <div className="space-y-2">
            {stats.referrals.map((referral, index) => (
              <div 
                key={referral.user_id || referral.email || `ref-${index}`}
                className="flex items-center justify-between p-3 bg-black/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#00F0FF] to-white flex items-center justify-center text-sm font-bold">
                    {referral.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{referral.name || 'Anonymous'}</div>
                    <div className="text-xs text-neutral-500">{referral.role}</div>
                  </div>
                </div>
                <div className="text-xs text-neutral-400">
                  {new Date(referral.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </main>
    </div>
  );
}

export default ReferralDashboard;
